/** Deal-Engine Script Navigator + Offer Sheet (v1 deScriptRender / deOfferSheetOpen).
 * A branching modal that walks the Text/Call playbook with the deal's numbers
 * pre-filled, a working-offer with +5%/MAO bumps, copy-ready node text, and the
 * offer sheet (Text 12) with "Mark offer made". Offer-sheet boilerplate (LLC /
 * email / title co.) lives in localStorage — your reusable header. */
import { useEffect, useMemo, useState } from "react";
import type { Lead } from "../../types";
import {
  DE_NODES, buildCtx, deNums, seedOffer, f$, deOfferSheetText, loadOfferCfg, saveOfferCfg,
  type Flow, type OfferCfg,
} from "./scripts";

export function ScriptNavigator({
  lead, fields, notes, now, outcomeLog, myName, initial, onField, onClose,
}: {
  lead: Lead;
  fields: Record<string, unknown> | undefined;
  notes: string;
  now: number;
  outcomeLog: unknown;
  myName: string;
  initial: { view: "script"; flow: Flow } | { view: "sheet" };
  onField: (address: string, key: string, value: string | null) => void;
  onClose: () => void;
}) {
  const N = useMemo(() => deNums(lead, fields, notes, now, outcomeLog), [lead, fields, notes, now, outcomeLog]);
  const [view, setView] = useState<"script" | "sheet">(initial.view);
  const [flow, setFlow] = useState<Flow>(initial.view === "script" ? initial.flow : "text");
  const [node, setNode] = useState<string>(initial.view === "script" ? (initial.flow === "call" ? "c1" : "t1") : "t1");
  const [hist, setHist] = useState<string[]>([]);
  const [offer, setOffer] = useState<number>(seedOffer(N));
  const [cfg, setCfg] = useState<OfferCfg>(loadOfferCfg());
  const [sheetText, setSheetText] = useState<string>("");
  const [copied, setCopied] = useState("");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  useEffect(() => { saveOfferCfg(cfg); }, [cfg]);
  useEffect(() => { if (view === "sheet") setSheetText(deOfferSheetText(lead, fields, offer, cfg, myName)); }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  const ctx = buildCtx(lead, fields, notes, now, outcomeLog, offer, myName);
  const cur = DE_NODES[node] ?? DE_NODES[flow === "call" ? "c1" : "t1"]!;

  const openFlow = (fl: Flow) => { setFlow(fl); setNode(fl === "call" ? "c1" : "t1"); setHist([]); setView("script"); };
  const go = (next: string) => {
    if (next === "BUMP5") { setOffer((o) => Math.round((o || 0) * 1.05)); return; }
    if (next === "BUMPMAO") { const m = N.off && N.off.mao != null ? N.off.mao : N.concl; if (m) setOffer(m); return; }
    if (next === "SHEET") { setSheetText(deOfferSheetText(lead, fields, offer, cfg, myName)); setView("sheet"); return; }
    if (next === "END") { onClose(); return; }
    setHist((h) => [...h, node]); setNode(next);
  };
  const back = () => setHist((h) => { if (!h.length) return h; setNode(h[h.length - 1]!); return h.slice(0, -1); });
  const copy = (text: string, tag: string) => { try { void navigator.clipboard?.writeText(text); setCopied(tag); setTimeout(() => setCopied(""), 1500); } catch { /* ignore */ } };
  const markOfferMade = () => {
    onField(lead.address, "offerMade", "Yes");
    if (offer) onField(lead.address, "offerPrice", f$(offer));
    onClose();
  };

  const chips: [string, string | number][] = [
    ["List", f$(N.list)], ["Zestimate", f$(N.zest)], ["Your ARV", f$(N.arv)], ["Your rehab", f$(N.rehab)],
    ["MAO", N.off && N.off.mao != null ? f$(N.off.mao) : "—"], ["Matrix", N.off ? f$(N.off.lo) + "–" + f$(N.off.hi) : "—"],
    ["DOM", ctx.dom], ["Agent", (fields?.["dispName"] as string) || "—"],
  ];

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="demodal">
        <div className="demodalhead">
          <h2>{view === "sheet" ? "📄 Offer Sheet" : flow === "call" ? "📞 Call Script" : "💬 Text Script"} <span className="sub">{String(lead.address).split(",")[0]}</span></h2>
          <button className="close" onClick={onClose}>×</button>
        </div>

        {view === "script" ? (
          <div className="demodalbody">
            <div className="dechips">{chips.map(([l, v]) => <span key={l} className="expcat">{l}: <b>{v}</b></span>)}</div>
            <div className="deofferrow">
              <span className="lbl">Working offer:</span>
              <input className="deofferin" value={offer || ""} inputMode="numeric" onChange={(e) => setOffer(Number(e.target.value.replace(/[^0-9]/g, "")) || 0)} />
              <div className="deflowtog">
                <button className={flow === "text" ? "on" : ""} onClick={() => openFlow("text")}>Text flow</button>
                <button className={flow === "call" ? "on" : ""} onClick={() => openFlow("call")}>Call flow</button>
              </div>
            </div>
            <div className="denode">
              <div className="denodehead"><span className="denodetag">{node}</span><button className="btn slim" onClick={() => copy(cur.t(ctx), "node")}>{copied === "node" ? "Copied ✓" : "Copy"}</button></div>
              <div className="denodetext">{cur.t(ctx)}</div>
              {cur.i ? <div className="denodetip">💡 {cur.i(ctx)}</div> : null}
            </div>
            <div className="deopts">
              <div className="deoptshd">What happened?</div>
              {cur.o.map(([label, next], i) => (
                <button key={i} className={"deopt" + (next === "SHEET" ? " sheet" : "")} onClick={() => go(next)}>{label} <span className="arr">→</span></button>
              ))}
            </div>
            <div className="defoot">
              <button onClick={back} disabled={!hist.length}>← Back</button>
              <button onClick={() => openFlow(flow)}>Restart flow</button>
              <button className="sheetlink" onClick={() => go("SHEET")}>Offer sheet →</button>
            </div>
          </div>
        ) : (
          <div className="demodalbody">
            <div className="desheetcfg">
              <input placeholder="LLC name" value={cfg.llc} onChange={(e) => setCfg({ ...cfg, llc: e.target.value })} />
              <input placeholder="LLC address" value={cfg.llcAddr} onChange={(e) => setCfg({ ...cfg, llcAddr: e.target.value })} />
              <input placeholder="Your email" value={cfg.email} onChange={(e) => setCfg({ ...cfg, email: e.target.value })} />
              <input placeholder="Title company (optional)" value={cfg.titleCo} onChange={(e) => setCfg({ ...cfg, titleCo: e.target.value })} />
            </div>
            <p className="sub">Details save on this device and prefill every future offer sheet. Refresh the text after changing them.</p>
            <textarea className="desheettext" rows={16} value={sheetText} onChange={(e) => setSheetText(e.target.value)} />
            <div className="expformacts">
              <button className="btn slim primary" onClick={() => copy(sheetText, "sheet")}>{copied === "sheet" ? "Copied ✓" : "Copy — text it to the agent"}</button>
              <button className="btn slim" onClick={() => setSheetText(deOfferSheetText(lead, fields, offer, cfg, myName))}>Refresh text</button>
              <button className="btn slim" onClick={markOfferMade}>✓ Mark offer made</button>
              <button className="btn slim" onClick={() => setView("script")}>← Back to script</button>
            </div>
            <p className="sub">"Mark offer made" sets Offer Made = Yes on the lead (arms the weekly re-submit trigger). Follow up on the SAME email thread each time.</p>
          </div>
        )}
      </div>
    </>
  );
}
